/**
 * Creating an account.
 *
 * The permissions are on this form rather than a step afterwards, and that is
 * deliberate: an account that exists before anyone has decided what it may do
 * is a window, however short, in which nobody has decided what it may do.
 */
import { useState, type FormEvent } from "react";
import { emptyGrants, type Grants } from "@dp/shared";
import { ApiError, api } from "../../lib/api.ts";
import { useAuth } from "../../lib/auth.tsx";
import { PermissionMatrix } from "./PermissionMatrix.tsx";

function suggestPassword(): string {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
  const bytes = crypto.getRandomValues(new Uint8Array(20));
  return Array.from(bytes, (byte) => alphabet[byte % alphabet.length]).join("");
}

export function NewAccountForm({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const { presets } = useAuth();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState(suggestPassword);
  const [isAdmin, setIsAdmin] = useState(false);
  const [grants, setGrants] = useState<Grants>(emptyGrants);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setProblem(null);
    setBusy(true);
    try {
      await api.post("/api/accounts", { email: email.trim(), name: name.trim(), password, isAdmin, grants });
      onDone();
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "Could not create this account.");
      setBusy(false);
    }
  }

  return (
    <form className="card" onSubmit={submit}>
      <div className="card-head"><h2>New account</h2></div>
      <div className="card-body stack">
        {problem && <div className="notice notice-bad">{problem}</div>}

        <div className="field-row">
          <div className="field">
            <label htmlFor="account-name">Name<span className="required-mark">*</span></label>
            <input id="account-name" className="control" value={name} required autoFocus
              onChange={(event) => setName(event.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="account-email">Email address<span className="required-mark">*</span></label>
            <input id="account-email" className="control" type="email" value={email} required
              onChange={(event) => setEmail(event.target.value)} />
            <span className="hint">This is what they sign in with.</span>
          </div>
        </div>

        <div className="field">
          <label htmlFor="account-password">First password<span className="required-mark">*</span></label>
          <div className="row">
            <input id="account-password" className="control" type="text" value={password} required minLength={12}
              onChange={(event) => setPassword(event.target.value)} />
            <button type="button" className="btn btn-sm" onClick={() => setPassword(suggestPassword())}>Suggest another</button>
          </div>
          <span className="hint">
            At least 12 characters. Shown in the clear so you can pass it on — they can change it once they are in.
          </span>
        </div>

        <label className="checkline">
          <input type="checkbox" checked={isAdmin} onChange={(event) => setIsAdmin(event.target.checked)} />
          <span>
            Administrator
            <div className="small muted">Can do everything, including managing accounts. Grant it sparingly.</div>
          </span>
        </label>

        <div className="field">
          <label>What they may do</label>
          <PermissionMatrix
            grants={grants} disabled={false} isAdmin={isAdmin} presets={presets} onChange={setGrants}
          />
          <span className="hint">Tick nothing and the account can sign in but open nothing.</span>
        </div>
      </div>

      <div className="card-foot">
        <button type="button" className="btn" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? "Creating…" : "Create account"}
        </button>
      </div>
    </form>
  );
}
